import { useEffect, useLayoutEffect, useRef, useState, memo } from "react";

// Code block style using CSS variables (theme-aware)
const getCodeBlockStyle = () => ({
  backgroundColor: 'var(--formulas-bg)',
  color: 'var(--text-primary)',
  borderRadius: '8px',
  padding: '16px',
  overflow: 'auto',
  fontSize: '14px',
  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, source-code-pro, monospace',
  lineHeight: '1.5',
  whiteSpace: 'pre' as const,
  wordBreak: 'normal' as const,
  wordWrap: 'normal' as const,
  tabSize: 2,
});

// Helper function to unescape HTML entities
const unescapeHtml = (text: string): string => {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
};

// Helper function to escape HTML entities (for safe innerHTML assignment)
const escapeHtml = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const LANGUAGE_MAP: Record<string, string> = {
  'js': 'javascript',
  'ts': 'typescript',
  'tsx': 'tsx',
  'jsx': 'jsx',
  'c#': 'csharp',
  'csharp': 'csharp',
  'py': 'python',
  'react': 'jsx',
};

// Lightweight syntax highlighter component
export const CodeHighlighter = memo<{ 
  code: string; 
  language: string; 
  showLineNumbers?: boolean;
}>(({ code, language, showLineNumbers = false }) => {
  const [prismLoaded, setPrismLoaded] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(localStorage.getItem('theme') || 'dark');
  const codeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Update theme when it changes
    const handleThemeChange = () => {
      const newTheme = localStorage.getItem('theme') || 'dark';
      setCurrentTheme(newTheme);
    };

    document.addEventListener('themeChanged', handleThemeChange);
    return () => document.removeEventListener('themeChanged', handleThemeChange);
  }, []);

  useEffect(() => {
    // Load Prism.js if not already loaded
    if (!window.Prism) {
      // Load Prism core
      const prismScript = document.createElement('script');
      prismScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/prism.min.js';
      prismScript.onload = () => {
        // Load language components
        const languages = ['javascript', 'typescript', 'csharp', 'sql', 'python', 'jsx', 'tsx', 'diff'];
        let loadedCount = 0;
        
        languages.forEach(lang => {
          const langScript = document.createElement('script');
          
          langScript.src = `https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/components/prism-${lang}.min.js`;
          langScript.onload = () => {
            loadedCount++;
            if (loadedCount === languages.length) {
              setPrismLoaded(true);
            }
          };
          langScript.onerror = () => {
            loadedCount++;
            if (loadedCount === languages.length) {
              setPrismLoaded(true);
            }
          };
          document.head.appendChild(langScript);
        });
      };
      document.head.appendChild(prismScript);
    } else {
      setPrismLoaded(true);
    }
  }, []);

  useEffect(() => {
    // Load appropriate CSS theme
    const loadPrismCSS = () => {
      // Remove existing Prism CSS
      const existingCSS = document.querySelector('link[href*="prism"]');
      if (existingCSS) {
        existingCSS.remove();
      }

      // Only load CDN CSS for light and dark themes
      if (currentTheme !== 'blonde') {
        const prismCSS = document.createElement('link');
        prismCSS.rel = 'stylesheet';
        prismCSS.href = currentTheme === 'light' 
          ? 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism.min.css'
          : 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism-tomorrow.min.css';
        document.head.appendChild(prismCSS);
      }
    };

    loadPrismCSS();
  }, [currentTheme]);

  // Update the <code> element's content directly via DOM manipulation.
  // This avoids React reconciliation replacing child nodes (which would destroy
  // any active text selection the user has inside the code block).
  useLayoutEffect(() => {
    const el = codeRef.current;
    if (!el) return;

    // Preserve active text selection within this element — skip update if
    // the user currently has text selected inside the code block.
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (el.contains(range.commonAncestorContainer)) {
        return;
      }
    }

    const unescapedCode = unescapeHtml(code);
    if (prismLoaded && window.Prism) {
      try {
        const prismLanguage = LANGUAGE_MAP[language.toLowerCase()] ?? language.toLowerCase();
        if (window.Prism.languages[prismLanguage]) {
          el.innerHTML = window.Prism.highlight(
            unescapedCode,
            window.Prism.languages[prismLanguage],
            prismLanguage
          );
        } else {
          el.innerHTML = escapeHtml(unescapedCode);
        }
      } catch (error) {
        console.warn('Prism highlighting failed:', error);
        el.innerHTML = escapeHtml(unescapedCode);
      }
    } else {
      el.textContent = unescapedCode;
    }
  }, [code, language, prismLoaded]);

  const codeBlockStyle = getCodeBlockStyle();

  // Custom syntax highlighting colors for blonde theme (uses CSS vars)
  const blondeTokenStyles = currentTheme === 'blonde' ? {
    '.token.comment, .token.prolog, .token.doctype, .token.cdata': { 
      color: 'var(--text-muted)', 
      fontStyle: 'italic' 
    },
    '.token.punctuation': { 
      color: 'var(--text-primary)' 
    },
    '.token.property, .token.tag, .token.constant, .token.symbol, .token.deleted': { 
      color: 'var(--accent-color)' 
    },
    '.token.boolean, .token.number': { 
      color: 'var(--warning-color)' 
    },
    '.token.selector, .token.attr-name, .token.string, .token.char, .token.builtin, .token.inserted': { 
      color: 'var(--success-color)' 
    },
    '.token.operator, .token.entity, .token.url, .language-css .token.string, .style .token.string, .token.variable': { 
      color: 'var(--text-secondary)' 
    },
    '.token.atrule, .token.attr-value, .token.function, .token.class-name': { 
      color: 'var(--accent-color)' 
    },
    '.token.keyword': { 
      color: 'var(--accent-hover)',
      fontWeight: 'bold' 
    },
    '.token.regex, .token.important': { 
      color: 'var(--warning-color)',
      fontWeight: 'bold' 
    },
    '.token.important, .token.bold': { 
      fontWeight: 'bold' 
    },
    '.token.italic': { 
      fontStyle: 'italic' 
    }
  } : {};

  const unescapedCode = unescapeHtml(code);
  const lines = unescapedCode.split('\n');
  const shouldShowLineNumbers = showLineNumbers && lines.length > 1;

  return (
    <>
      {/* Inline styles for blonde theme only */}
      {currentTheme === 'blonde' && (
        <style>{`
          ${Object.entries(blondeTokenStyles).map(([selector, styles]) => 
            `${selector} { ${Object.entries(styles as Record<string, string>).map(([prop, value]) => `${prop}: ${value}`).join('; ')} }`
          ).join('\n')}
        `}</style>
      )}
      <pre style={codeBlockStyle}>
        {shouldShowLineNumbers && (
          <div style={{
            position: 'absolute',
            left: '0',
            top: '16px',
            bottom: '16px',
            width: '40px',
            color: 'var(--text-muted)',
            fontSize: '12px',
            lineHeight: '1.5',
            textAlign: 'right',
            paddingRight: '8px',
            userSelect: 'none',
            fontFamily: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            paddingTop: '0',
          }}>
            {lines.map((_, index) => (
              <span key={index} style={{ minHeight: '21px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                {index + 1}
              </span>
            ))}
          </div>
        )}
        <code 
          ref={codeRef}
          className={`language-${language}`}
          style={{ 
            paddingLeft: shouldShowLineNumbers ? '48px' : '0',
            display: 'block'
          }}
        />
      </pre>
    </>
  );
});